import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logger from "./logger";

/**
 * Generate a Client Quote PDF - Xero Invoice Style
 * @param {Object} options
 * @param {Object} options.jobData - The job data (name, date, client, etc.)
 * @param {Object|null} options.snapshot - Approved snapshot (if exists)
 * @param {Object|null} options.liveQuote - Live quote data (used if no snapshot)
 * @param {boolean} options.showDesignFeeToClient - Whether design fee is shown to client
 * @param {Object|null} options.settings - Business settings for branding
 * @param {string|null} options.jobNumber - Job number (AA-0001 format)
 */
export function generateClientQuotePDF({
  jobData,
  snapshot,
  liveQuote,
  showDesignFeeToClient,
  settings,
  jobNumber,
  version,
  fileNameSuffix = "",
  methodNames = {},
}) {
  logger.log("generateClientQuotePDF called with:", { 
    hasJobData: !!jobData, 
    hasSnapshot: !!snapshot, 
    hasLiveQuote: !!liveQuote,
    jobNumber 
  });
  
  // Early validation - need either snapshot or liveQuote
  if (!snapshot && !liveQuote) {
    console.error("PDF generation failed: No snapshot or live quote data available");
    throw new Error("No quote data available to generate PDF");
  }
  
  try {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Business branding from settings
    const businessName = settings?.business_name || "Alpine Armour";
    const businessEmail = settings?.business_email || "";
    const businessPhone = settings?.business_phone || "";
    const businessWebsite = settings?.business_website || "";
    const businessLogoUrl = settings?.business_logo_url || "";

    // Use snapshot if available, otherwise use live quote
    const useSnapshot = !!snapshot;
    const source = useSnapshot ? snapshot : null;

    // Prepare data
    const jobName = useSnapshot ? source.job_name : jobData?.job_name || "Untitled";
    const quoteDate = useSnapshot
      ? source.quote_date
      : jobData?.quote_date || new Date().toISOString().split("T")[0];
  
  // Calculate or use frozen valid_until
  let validUntil = "";
  if (useSnapshot && source.valid_until) {
    validUntil = source.valid_until;
  } else if (quoteDate) {
    // Calculate: Quote Date + 14 days
    try {
      const dateObj = new Date(quoteDate);
      dateObj.setDate(dateObj.getDate() + 14);
      validUntil = dateObj.toISOString().split("T")[0];
    } catch (e) {
      validUntil = "";
    }
  }

  // Client details - updated for business/contact separation
  const clientBusinessName = useSnapshot 
    ? source.client_business_name 
    : jobData.client?.business_name || "";
  const clientContactName = useSnapshot 
    ? source.client_contact_name 
    : jobData.client?.contact_name || "";
  const clientNameLegacy = useSnapshot 
    ? source.client_name 
    : jobData.client?.name || "";
  const clientEmail = useSnapshot 
    ? source.client_email 
    : jobData.client?.email || "";
  const clientPhone = useSnapshot 
    ? source.client_phone 
    : jobData.client?.phone || "";

  // Get line items
  let lineItems = [];
  let subtotalExGst = 0;
  let designFeeSell = 0;
  let showDesignFee = false;
  let smallOrderSurcharge = 0;
  let shipping = 0;
  let gst = 0;
  let totalIncGst = 0;

  logger.log("PDF data source:", useSnapshot ? "snapshot" : "liveQuote");

  if (useSnapshot) {
    lineItems = source.garment_lines || [];
    subtotalExGst = source.subtotal_ex_gst || 0;
    designFeeSell = source.design_fee_sell || 0;
    showDesignFee = source.design_shown_to_client && designFeeSell > 0;
    // For fees - use snapshot if available, otherwise fall back to live data
    // This handles old snapshots that don't have these fields
    smallOrderSurcharge = source.small_order_surcharge ?? (liveQuote?.small_order_surcharge || 0);
    shipping = source.shipping ?? (jobData?.shipping || 0);
    gst = source.gst || 0;
    totalIncGst = source.total_inc_gst || 0;
    logger.log("Snapshot fees - smallOrderSurcharge:", smallOrderSurcharge, "shipping:", shipping, "designFee:", designFeeSell, "showDesignFee:", showDesignFee);
  } else if (liveQuote) {
    lineItems = (liveQuote.garment_lines || []).map((line) => ({
      id: line.id,
      name: line.name,
      colour: line.colour,
      display_name: line.display_name,
      decoration_summary: line.decoration_summary || "",
      client_note: line.client_note || "",
      quantity: line.quantity,
      unit_sell_price: line.unit_sell_price,
      line_total: line.line_total,
    }));
    subtotalExGst = liveQuote.production_total_ex_gst || 0;
    designFeeSell = liveQuote.design_fee_sell || 0;
    showDesignFee = showDesignFeeToClient && designFeeSell > 0;
    smallOrderSurcharge = liveQuote.small_order_surcharge || 0;
    shipping = jobData?.shipping || 0;
    gst = liveQuote.gst || 0;
    totalIncGst = liveQuote.total_inc_gst || 0;
    logger.log("LiveQuote fees - smallOrderSurcharge:", smallOrderSurcharge, "shipping:", shipping, "designFee:", designFeeSell, "showDesignFee:", showDesignFee);
  }

  // Colors - Xero-like clean style
  const charcoal = [33, 37, 41]; // Dark text
  const mediumGray = [108, 117, 125]; // Labels/secondary text
  const lightGray = [222, 226, 230]; // Borders/dividers
  const black = [0, 0, 0];

  const pageWidth = 210;
  const pageHeight = 297;
  const marginLeft = 15;
  const marginRight = 195;

  // ==================== HEADER SECTION ====================
  let yPos = 15;

  // --- LEFT SIDE: Logo + Business Contact ---
  let logoEndY = yPos;
  const logoMaxHeight = 20;
  
  if (businessLogoUrl && businessLogoUrl.startsWith("data:image")) {
    try {
      doc.addImage(businessLogoUrl, "AUTO", marginLeft, yPos, 0, logoMaxHeight);
      logoEndY = yPos + logoMaxHeight + 3;
    } catch (e) {
      logger.warn("Failed to add logo to PDF:", e);
    }
  }

  // Business contact stack under logo
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...mediumGray);
  
  let contactY = logoEndY + 2;
  if (businessName && !businessLogoUrl) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...charcoal);
    doc.setFontSize(12);
    doc.text(businessName, marginLeft, contactY);
    contactY += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...mediumGray);
  }
  if (businessEmail) {
    doc.text(businessEmail, marginLeft, contactY);
    contactY += 4;
  }
  if (businessPhone) {
    doc.text(businessPhone, marginLeft, contactY);
    contactY += 4;
  }
  if (businessWebsite) {
    doc.text(businessWebsite, marginLeft, contactY);
    contactY += 4;
  }

  // --- RIGHT SIDE: QUOTE heading + meta info ---
  // Large "QUOTE" heading
  doc.setFontSize(28);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...charcoal);
  doc.text("QUOTE", marginRight, yPos + 8, { align: "right" });

  // Reference, Date, Valid Until - right aligned
  let metaY = yPos + 18;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...mediumGray);

  // Job Number (if exists)
  if (jobNumber) {
    doc.text("Job #:", marginRight - 45, metaY, { align: "right" });
    doc.setTextColor(...charcoal);
    doc.setFont("helvetica", "bold");
    doc.text(jobNumber, marginRight, metaY, { align: "right" });
    doc.setFont("helvetica", "normal");
    metaY += 5;
    doc.setTextColor(...mediumGray);
  }

  // Version (client-facing) — e.g. "V2"
  if (version) {
    doc.text("Version:", marginRight - 45, metaY, { align: "right" });
    doc.setTextColor(...charcoal);
    doc.setFont("helvetica", "bold");
    doc.text(`V${version}`, marginRight, metaY, { align: "right" });
    doc.setFont("helvetica", "normal");
    metaY += 5;
    doc.setTextColor(...mediumGray);
  }

  // Date
  doc.text("Date:", marginRight - 45, metaY, { align: "right" });
  doc.setTextColor(...charcoal);
  doc.text(formatDate(quoteDate), marginRight, metaY, { align: "right" });
  metaY += 5;

  // Valid Until
  doc.setTextColor(...mediumGray);
  doc.text("Valid Until:", marginRight - 45, metaY, { align: "right" });
  doc.setTextColor(...charcoal);
  doc.text(formatDate(validUntil), marginRight, metaY, { align: "right" });
  metaY += 5;

  // Reference (Job Name)
  doc.setTextColor(...mediumGray);
  doc.text("Reference:", marginRight - 45, metaY, { align: "right" });
  doc.setTextColor(...charcoal);
  doc.setFont("helvetica", "bold");
  const truncatedJobName = jobName.length > 30 ? jobName.substring(0, 30) + "..." : jobName;
  doc.text(truncatedJobName, marginRight, metaY, { align: "right" });

  // --- DIVIDER LINE ---
  yPos = Math.max(contactY, metaY) + 8;
  doc.setDrawColor(...lightGray);
  doc.setLineWidth(0.5);
  doc.line(marginLeft, yPos, marginRight, yPos);

  // ==================== BILL TO SECTION ====================
  yPos += 8;

  const hasClientInfo = clientBusinessName || clientContactName || clientNameLegacy || clientEmail;

  if (hasClientInfo) {
    // "Bill To" label - small caps style
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...mediumGray);
    doc.text("BILL TO", marginLeft, yPos);
    yPos += 5;

    // Client details - business/contact separation
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...charcoal);
    
    if (clientBusinessName) {
      // Business Name exists - show it prominently
      doc.text(clientBusinessName, marginLeft, yPos);
      yPos += 5;
      if (clientContactName) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...mediumGray);
        doc.text(`Attn: ${clientContactName}`, marginLeft, yPos);
        yPos += 4;
      }
    } else if (clientContactName) {
      // Only Contact Name - show it as primary
      doc.text(clientContactName, marginLeft, yPos);
      yPos += 5;
    } else if (clientNameLegacy) {
      // Legacy fallback
      doc.text(clientNameLegacy, marginLeft, yPos);
      yPos += 5;
    }
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...mediumGray);
    if (clientEmail) {
      doc.text(clientEmail, marginLeft, yPos);
      yPos += 4;
    }
    if (clientPhone) {
      doc.text(clientPhone, marginLeft, yPos);
      yPos += 4;
    }
    yPos += 5;
  }

  // ==================== LINE ITEMS TABLE ====================
  yPos += 5;

  // Custom multi-line, multi-weight description rendering (#10):
  //   - Garment name  -> bold heading
  //   - "Colour:"      -> garment colour on its own line
  //   - "Decoration:" -> bold heading, each print group on its own line
  //   - "Note:"       -> bold heading, client note below
  const DESC_LINE_H = 4.0; // mm per rendered line
  const DESC_CONTENT_WIDTH = 87; // column 0 cellWidth (95) minus L/R padding (8)
  // Parallel array of render-lines for each garment row (index-aligned with garment line items)
  const lineMeta = [];

  // Current garment notes/colours (from live job) — used to keep the PDF in sync even
  // when rendering from a frozen snapshot (client notes are descriptive, not priced).
  const currentNotesById = {};
  const currentColourById = {};
  (jobData?.garments || []).forEach((g) => {
    if (g && g.id) {
      currentNotesById[g.id] = (g.client_note || "").trim();
      currentColourById[g.id] = g.colour || "";
    }
  });

  // Enriched per-garment data (Sizes line + "Placement / Size Tier / Print Method"
  // decoration lines) built from jobData — same data sources as the Xero export.
  const garmentsById = {};
  (jobData?.garments || []).forEach((g) => {
    if (g && g.id) garmentsById[g.id] = g;
  });
  const artworkGroups = jobData?.artwork_groups || [];
  const sizesLineFor = (gid) => {
    const g = garmentsById[gid];
    const sb = (g && g.size_breakdown) || {};
    return Object.entries(sb)
      .filter(([, q]) => q && Number(q) > 0)
      .map(([s, q]) => `${s}/${Number(q)}`)
      .join(", ");
  };
  const decoLinesFor = (gid) => {
    const out = [];
    artworkGroups.forEach((ag) => {
      const alloc = (ag.allocations || []).find(
        (a) => a.garment_id === gid && (a.placement_count || 0) > 0
      );
      if (!alloc) return;
      const placement = (ag.placement_name || "").trim() || "Print";
      const tier = (ag.size_tier || "").trim();
      const method = ag.print_method || "";
      const disp = methodNames[method] || method;
      out.push([placement, tier, disp].filter(Boolean).join(" / "));
    });
    return out;
  };

  const tableBody = lineItems.map((line) => {
    const garmentName = line.name || line.display_name || "";
    const colour = line.colour || currentColourById[line.id] || "";
    const enrichedDeco = line.id != null ? decoLinesFor(line.id) : [];
    const fallbackDeco = line.decoration_summary
      ? String(line.decoration_summary).split(/,\s*/).map((d) => d.trim()).filter(Boolean)
      : [];
    const decorations = enrichedDeco.length > 0 ? enrichedDeco : fallbackDeco;
    const sizesLine = line.id != null ? sizesLineFor(line.id) : "";
    // Prefer the current (live) client note so recently-added notes always appear,
    // falling back to whatever was frozen in the snapshot.
    const currentNote = line.id != null ? currentNotesById[line.id] : undefined;
    const note = currentNote !== undefined && currentNote !== ""
      ? currentNote
      : (line.client_note && String(line.client_note).trim() ? String(line.client_note).trim() : "");

    const renderLines = [];
    // Garment name (bold) — wrap to column width
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.splitTextToSize(garmentName, DESC_CONTENT_WIDTH).forEach((t) =>
      renderLines.push({ t, bold: true, indent: 0 })
    );
    // Garment colour on its own line ("Colour:" bold label + value)
    if (colour) {
      renderLines.push({ label: "Colour: ", t: colour, indent: 0 });
    }
    // Sizes on its own line ("Sizes:" bold label + value)
    if (sizesLine) {
      renderLines.push({ label: "Sizes: ", t: sizesLine, indent: 0 });
    }
    // Decoration heading + one print group per line
    if (decorations.length > 0) {
      renderLines.push({ t: "Decoration:", bold: true, indent: 0 });
      doc.setFont("helvetica", "normal");
      decorations.forEach((d) => {
        doc.splitTextToSize(`-  ${d}`, DESC_CONTENT_WIDTH - 3).forEach((t) =>
          renderLines.push({ t, bold: false, indent: 3 })
        );
      });
    }
    // Client note heading + text
    if (note) {
      renderLines.push({ t: "Note:", bold: true, indent: 0 });
      doc.setFont("helvetica", "normal");
      doc.splitTextToSize(note, DESC_CONTENT_WIDTH - 3).forEach((t) =>
        renderLines.push({ t, bold: false, indent: 3 })
      );
    }
    lineMeta.push(renderLines);

    return [
      "", // description drawn manually in didDrawCell
      line.quantity.toString(),
      formatCurrency(line.unit_sell_price),
      formatCurrency(line.line_total),
    ];
  });

  // Add design fee as a line item if shown
  if (showDesignFee) {
    logger.log("Adding Design fee to PDF:", designFeeSell);
    tableBody.push([
      "Design / Artwork Setup",
      "1",
      formatCurrency(designFeeSell),
      formatCurrency(designFeeSell),
    ]);
  }

  // Add small order surcharge as a line item if applicable
  if (smallOrderSurcharge > 0) {
    logger.log("Adding Small Order Fee to PDF:", smallOrderSurcharge);
    tableBody.push([
      "Small Order Setup Fee",
      "1",
      formatCurrency(smallOrderSurcharge),
      formatCurrency(smallOrderSurcharge),
    ]);
  } else {
    logger.log("Small Order Fee NOT added - value is:", smallOrderSurcharge);
  }

  // Add shipping as a line item if applicable
  if (shipping > 0) {
    logger.log("Adding Shipping to PDF:", shipping);
    tableBody.push([
      "Shipping & Handling",
      "1",
      formatCurrency(shipping),
      formatCurrency(shipping),
    ]);
  } else {
    logger.log("Shipping NOT added - value is:", shipping);
  }

  logger.log("Final tableBody rows:", tableBody.length);

  autoTable(doc, {
    startY: yPos,
    head: [["Description", "Qty", "Unit Price", "Amount"]],
    body: tableBody,
    theme: "plain",
    headStyles: {
      fillColor: [248, 249, 250], // Very light gray background for header
      textColor: charcoal,
      fontStyle: "bold",
      fontSize: 9,
      cellPadding: { top: 4, right: 4, bottom: 4, left: 4 },
      lineWidth: 0, // No cell borders
    },
    bodyStyles: {
      fontSize: 9,
      textColor: charcoal,
      cellPadding: { top: 4, right: 4, bottom: 4, left: 4 },
      lineWidth: 0, // No cell borders
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255],
    },
    columnStyles: {
      0: { cellWidth: 95 }, // Description
      1: { cellWidth: 20, halign: "center" }, // Qty
      2: { cellWidth: 35, halign: "right" }, // Unit Price
      3: { cellWidth: 35, halign: "right" }, // Amount
    },
    margin: { left: marginLeft, right: 15, bottom: 30 }, // Reserve space at bottom
    tableLineWidth: 0, // No outer table border
    showHead: 'everyPage', // Repeat header on every page for multi-page quotes
    didParseCell: function(data) {
      // Reserve vertical space for the manually-drawn multi-line description
      if (data.row && data.row.section === 'body' && data.column.index === 0) {
        const rl = lineMeta[data.row.index];
        if (rl && rl.length) {
          data.cell.styles.minCellHeight = 8 + rl.length * DESC_LINE_H;
        }
      }
    },
    didDrawCell: function(data) {
      // Draw subtle bottom border for body rows only (not header)
      if (data.row && data.row.section === 'body') {
        doc.setDrawColor(230, 230, 230); // Very subtle gray
        doc.setLineWidth(0.15);
        const y = data.cell.y + data.cell.height;
        // Only draw for the first column to create a single line across
        if (data.column.index === 0) {
          doc.line(marginLeft, y, marginRight, y);
        }
      }
      // Manually render the formatted description (bold headings + line breaks)
      if (data.row && data.row.section === 'body' && data.column.index === 0) {
        const rl = lineMeta[data.row.index];
        if (rl && rl.length) {
          const cx = data.cell.x + 4;
          let cy = data.cell.y + 4 + 3.0; // top padding + first-line baseline
          doc.setFontSize(9);
          doc.setTextColor(...charcoal);
          rl.forEach((item) => {
            if (item.label) {
              // Bold label + normal value on the same line (e.g. "Colour: Navy")
              doc.setFont("helvetica", "bold");
              doc.text(item.label, cx + (item.indent || 0), cy);
              const lw = doc.getTextWidth(item.label);
              doc.setFont("helvetica", "normal");
              doc.text(item.t, cx + (item.indent || 0) + lw, cy);
            } else {
              doc.setFont("helvetica", item.bold ? "bold" : "normal");
              doc.text(item.t, cx + (item.indent || 0), cy);
            }
            cy += DESC_LINE_H;
          });
          // Restore defaults for subsequent cells
          doc.setFont("helvetica", "normal");
          doc.setTextColor(...charcoal);
        }
      }
    },
    didDrawPage: function(data) {
      // Add page number to each page
      const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...mediumGray);
      doc.text(`Page ${pageNum}`, marginRight, pageHeight - 10, { align: "right" });
    },
  });

  // ==================== TOTALS BLOCK ====================
  // Calculate space needed for totals + footer (approximately 60mm)
  const totalsHeight = 55;
  yPos = doc.lastAutoTable.finalY + 10;
  
  // Check if totals will fit on current page - if not, add new page
  if (yPos + totalsHeight > pageHeight - 20) {
    doc.addPage();
    yPos = 25; // Reset Y position for new page
  }

  // More whitespace between table and totals
  yPos += 5;

  // Right margin spacing (totalsX is marginRight which is 195, giving ~15mm from edge)
  const totalsX = marginRight;
  const totalsLabelX = marginRight - 55; // Slightly more spacing

  // Subtotal (includes all line items shown)
  let displaySubtotal = subtotalExGst;
  if (showDesignFee) displaySubtotal += designFeeSell;
  if (smallOrderSurcharge > 0) displaySubtotal += smallOrderSurcharge;
  if (shipping > 0) displaySubtotal += shipping;
  
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...mediumGray);
  doc.text("Subtotal", totalsLabelX, yPos, { align: "right" });
  doc.setTextColor(...charcoal);
  doc.text(formatCurrency(displaySubtotal), totalsX, yPos, { align: "right" });

  // GST
  yPos += 7;
  doc.setTextColor(...mediumGray);
  doc.text("GST (10%)", totalsLabelX, yPos, { align: "right" });
  doc.setTextColor(...charcoal);
  doc.text(formatCurrency(gst), totalsX, yPos, { align: "right" });

  // Subtle divider before total
  yPos += 5;
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(totalsLabelX - 5, yPos, totalsX, yPos);

  // TOTAL (inc GST) - bold and prominent, stronger typography
  yPos += 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...black); // Darker for emphasis
  doc.text("TOTAL AUD", totalsLabelX, yPos, { align: "right" });
  doc.setFontSize(14);
  doc.text(formatCurrency(totalIncGst), totalsX, yPos, { align: "right" });

  // ==================== FOOTER ====================
  // Footer is placed after totals, not at a fixed position
  yPos += 15;
  
  // Check if footer fits on current page
  if (yPos + 20 > pageHeight - 10) {
    doc.addPage();
    yPos = 25;
  }

  // Footer divider
  doc.setDrawColor(...lightGray);
  doc.setLineWidth(0.3);
  doc.line(marginLeft, yPos, marginRight, yPos);
  yPos += 5;

  // Footer text
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...mediumGray);
  doc.text("Prices exclude GST unless stated otherwise.", marginLeft, yPos);
  doc.text(`Quote valid until ${formatDate(validUntil)}.`, marginLeft, yPos + 4);

  // Update page numbers on all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...mediumGray);
    doc.text(`Page ${i} of ${totalPages}`, marginRight, pageHeight - 10, { align: "right" });
  }

  // ==================== SAVE ====================
  const sanitizedJobName = (jobName || "Quote").replace(/[^a-zA-Z0-9]/g, "_");
  const fileName = `AlpineArmour_Quote_${sanitizedJobName}${fileNameSuffix}_${quoteDate}.pdf`;
  
  logger.log("Attempting to save PDF:", fileName);
  
  // Use blob approach for reliable downloads across all browsers
  try {
    const pdfBlob = doc.output('blob');
    const url = URL.createObjectURL(pdfBlob);
    
    // Create and configure the download link
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    
    // Append to body
    document.body.appendChild(link);
    
    // Trigger the download with a small delay for browser compatibility
    setTimeout(() => {
      link.click();
      
      // Cleanup after a delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        logger.log("PDF download cleanup complete");
      }, 100);
    }, 0);
    
    logger.log("PDF download triggered for:", fileName);
  } catch (downloadError) {
    console.error("PDF download error:", downloadError);
    // Fallback: open in new window
    const pdfDataUri = doc.output('datauristring');
    window.open(pdfDataUri, '_blank');
    logger.log("PDF opened in new window as fallback");
  }

  return fileName;
  } catch (error) {
    console.error("PDF generation error:", error);
    throw error;
  }
}

// Helper functions
function formatCurrency(value) {
  if (value === null || value === undefined) return "$0.00";
  return `$${parseFloat(value).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch (e) {
    return dateStr;
  }
}

// Note: Production Sheet PDF is now generated server-side via /api/jobs/{job_id}/production-sheet
